import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FileSpreadsheet } from 'lucide-react'
import { ObligacionesRecurrentesTable } from './ObligacionesRecurrentesTable'
import { ObligacionRecurrenteFormDialog } from './ObligacionRecurrenteFormDialog'
import {
  useObligacionesRecurrentes,
  useCrearObligacionRecurrente,
  useActualizarObligacionRecurrente,
  useEliminarObligacionRecurrente,
  useToggleObligacionRecurrente,
} from '@/hooks/useObligacionesRecurrentes'
import { useGenerarBalances } from '@/hooks/useCalendario'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import type { ObligacionRecurrente, ObligacionRecurrenteConClientes } from '@/services/obligacionesRecurrentesService'
import type { ObligacionRecurrenteFormValues } from './forms/obligacionRecurrenteSchema'

export function ObligacionesRecurrentesTab() {
  const currentYear = new Date().getFullYear()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [obligacionEditar, setObligacionEditar] = useState<ObligacionRecurrenteConClientes | null>(null)
  const [obligacionEliminar, setObligacionEliminar] = useState<ObligacionRecurrente | null>(null)
  const [borrarVencimientosPendientes, setBorrarVencimientosPendientes] = useState(true)
  const [yearBalances, setYearBalances] = useState(String(currentYear))

  const { data: obligaciones = [], isLoading } = useObligacionesRecurrentes()
  const crearMutation = useCrearObligacionRecurrente()
  const actualizarMutation = useActualizarObligacionRecurrente()
  const eliminarMutation = useEliminarObligacionRecurrente()
  const toggleMutation = useToggleObligacionRecurrente()
  const generarBalances = useGenerarBalances()
  const { organizacionId } = useOrganizacion()

  const handleNueva = () => {
    setObligacionEditar(null)
    setDialogOpen(true)
  }

  const handleEditar = (obligacion: ObligacionRecurrenteConClientes) => {
    setObligacionEditar(obligacion)
    setDialogOpen(true)
  }

  const handleSubmit = async (data: ObligacionRecurrenteFormValues) => {
    if (obligacionEditar) {
      await actualizarMutation.mutateAsync({ id: obligacionEditar.id, data })
    } else {
      await crearMutation.mutateAsync(data)
    }
  }

  const handleEliminar = (obligacion: ObligacionRecurrente) => {
    setBorrarVencimientosPendientes(true) // reset default
    setObligacionEliminar(obligacion)
  }

  const handleConfirmarEliminar = async () => {
    if (obligacionEliminar) {
      await eliminarMutation.mutateAsync({
        id: obligacionEliminar.id,
        borrarVencimientosPendientes,
      })
      setObligacionEliminar(null)
    }
  }

  const handleToggle = (obligacion: ObligacionRecurrente) => {
    toggleMutation.mutate({ id: obligacion.id, activo: !obligacion.activo })
  }

  return (
    <div className="space-y-6">
      <ObligacionesRecurrentesTable
        obligaciones={obligaciones}
        isLoading={isLoading}
        onNueva={handleNueva}
        onEditar={handleEditar}
        onEliminar={handleEliminar}
        onToggle={handleToggle}
      />

      {/* Balances — regeneración manual (fallback) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-base">Balances</CardTitle>
              <CardDescription>
                Los vencimientos se generan automáticamente al asignar el impuesto Balances a un cliente
                o al cambiar su cierre de ejercicio (cierre + 4 meses). Este botón es solo para regenerar
                manualmente años anteriores o casos especiales.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Select value={yearBalances} onValueChange={setYearBalances}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => organizacionId && generarBalances.mutate({ organizacionId, year: Number(yearBalances) })}
            disabled={generarBalances.isPending || !organizacionId}
          >
            {generarBalances.isPending ? 'Regenerando...' : 'Regenerar manualmente'}
          </Button>
        </CardContent>
      </Card>

      <ObligacionRecurrenteFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        obligacion={obligacionEditar}
        onSubmit={handleSubmit}
        isLoading={crearMutation.isPending || actualizarMutation.isPending}
      />

      <AlertDialog
        open={!!obligacionEliminar}
        onOpenChange={(open) => { if (!open) setObligacionEliminar(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar obligación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar &quot;{obligacionEliminar?.nombre}&quot;? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Opción de cascade */}
          <div className="flex items-start gap-3 rounded-md border p-3 bg-muted/50">
            <Checkbox
              id="borrar-vencimientos"
              checked={borrarVencimientosPendientes}
              onCheckedChange={(checked) => setBorrarVencimientosPendientes(checked === true)}
            />
            <div className="space-y-1">
              <Label htmlFor="borrar-vencimientos" className="text-sm font-medium cursor-pointer">
                También eliminar vencimientos pendientes generados
              </Label>
              <p className="text-xs text-muted-foreground">
                Se eliminarán los vencimientos pendientes (no presentados, no pagados) generados por esta obligación.
                Los vencimientos ya presentados o pagados no se tocan.
              </p>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarEliminar}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={eliminarMutation.isPending}
            >
              {eliminarMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
